import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { MainLayoutComponent } from './shared/layout/main-layout/main-layout.component';
import { roleGuard } from './core/guards/role.guard';
import { LoginComponent } from './features/auth/login/login.component';
import { LAUNDRY_MANAGER, LAUNDRY_STAFF } from './core/utils/constant';

export const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
  },
  {
    path: 'print/invoices/:invoiceId',
    loadComponent: () =>
      import(
        './features/print/receipt-print-page/receipt-print-page.component'
      ).then((m) => m.ReceiptPrintPageComponent),
    canActivate: [authGuard, roleGuard],
    data: { documentType: 'invoice' },
  },
  {
    path: 'print/orders/:orderNumber',
    loadComponent: () =>
      import(
        './features/print/receipt-print-page/receipt-print-page.component'
      ).then((m) => m.ReceiptPrintPageComponent),
    canActivate: [authGuard, roleGuard],
    data: { documentType: 'order' },
  },
  {
    path: 'dashboard',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [roleGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/home.component').then((m) => m.HomeComponent),
        canActivate: [roleGuard],
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            (m) => m.ProfileComponent
          ),
        canActivate: [roleGuard],
        data: { breadcrumbs: [{ label: 'shared/profile', link: 'profile' }] },
      },
      {
        path: 'laundry-details',
        loadComponent: () =>
          import('./features/laundry-details/laundry-details.component').then(
            (m) => m.LaundryDetailsComponent
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/laundryDetails', link: 'laundry-details' },
          ],
        },
      },
      {
        path: 'branches',
        loadChildren: () =>
          import('./features/branches/branches.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/branches', link: 'laundries/branches' },
          ],
        },
      },
      {
        path: 'collection-points',
        loadChildren: () =>
          import('./features/collection-points/collection-points.routes').then(
            (m) => m.routes
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/collectionPoints', link: 'collection-points' },
          ],
        },
      },
      {
        path: 'addition-requests',
        loadChildren: () =>
          import('./features/addition-requests/addition-requests.routes').then(
            (m) => m.routes
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/additionRequests', link: 'addition-requests' },
          ],
        },
      },
      {
        path: 'employees',
        loadChildren: () =>
          import('./features/employees/employees.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [{ label: 'shared/employees', link: 'employees' }],
        },
      },
      {
        path: 'communication',
        loadChildren: () =>
          import('./features/communication/communication.routes').then(
            (m) => m.routes
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/communication', link: 'communication' },
          ],
        },
      },
      {
        path: 'audit-history',
        loadComponent: () =>
          import('./features/audit-history/audit-history.component').then(
            (m) => m.AuditHistoryComponent
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/auditHistory', link: 'audit-history' },
          ],
        },
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./features/services/services.component').then(
            (m) => m.ServicesComponent
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/services', link: 'laundries/services' },
          ],
        },
      },
      {
        path: 'orders',
        loadChildren: () =>
          import('./features/orders/orders.routes').then((m) => m.routes),
        data: { breadcrumbs: [{ label: 'shared/orders', link: 'orders' }] },
      },
      {
        path: 'delivery-orders',
        loadComponent: () =>
          import('./features/delivery-orders/delivery-orders.component').then(
            (m) => m.DeliveryOrdersComponent
          ),
        data: {
          allowedRoles: [LAUNDRY_MANAGER, LAUNDRY_STAFF],
          excludeCollectionPointOnly: true,
          breadcrumbs: [
            { label: 'shared/deliveryOrders', link: 'delivery-orders' },
          ],
        },
      },
      {
        path: 'bills',
        loadComponent: () =>
          import('./features/bills/bills.component').then(
            (m) => m.BillsComponent
          ),
        canActivate: [roleGuard],
        data: { breadcrumbs: [{ label: 'shared/bills', link: 'bills' }] },
      },
      {
        path: 'packages/overview',
        loadChildren: () =>
          import('./features/packages/overview-packages/packages.routes').then(
            (m) => m.routes
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/overviewPackages', link: 'packages/overview' },
          ],
        },
      },
      {
        path: 'packages/subscriptions',
        loadComponent: () =>
          import(
            './features/packages/packages-subscriptions/packages-subscriptions.component'
          ).then((m) => m.PackagesSubscriptionsComponent),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            {
              label: 'shared/packagesSubscriptions',
              link: 'packages/subscriptions',
            },
          ],
        },
      },
      {
        path: 'packages/offers-increases',
        loadChildren: () =>
          import(
            './features/packages/offers-increases/offers-increases.routes'
          ).then((m) => m.routes),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            {
              label: 'shared/offersIncreases',
              link: 'packages/offers-increases',
            },
          ],
        },
      },
      {
        path: 'attachments',
        loadComponent: () =>
          import('./features/attachments/attachments.component').then(
            (m) => m.AttachmentsComponent
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [{ label: 'shared/attachments', link: 'attachments' }],
        },
      },
      {
        path: 'images',
        loadComponent: () =>
          import('./features/images/images.component').then(
            (m) => m.ImagesComponent
          ),
        canActivate: [roleGuard],
        data: { breadcrumbs: [{ label: 'shared/gallery', link: 'images' }] },
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./features/laundry-reports/laundry-reports.component').then(
            (m) => m.LaundryReportsComponent
          ),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [{ label: 'laundries/laundryReports', link: 'reports' }],
        },
      },
      {
        path: 'cashier-collection-report',
        loadComponent: () =>
          import(
            './features/cashier-collection-report/cashier-collection-report.component'
          ).then((m) => m.CashierCollectionReportComponent),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            {
              label: 'cashierCollection/title',
              link: 'cashier-collection-report',
            },
          ],
        },
      },
      {
        path: 'laundry-daily-order-status-report',
        loadComponent: () =>
          import(
            './features/laundry-daily-order-status-report/laundry-daily-order-status-report.component'
          ).then((m) => m.LaundryDailyOrderStatusReportComponent),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            {
              label: 'dailyOrderStatus/title',
              link: 'laundry-daily-order-status-report',
            },
          ],
        },
      },
      {
        path: 'credit-sales-report',
        loadComponent: () =>
          import(
            './features/credit-sales-report/credit-sales-report.component'
          ).then((m) => m.CreditSalesReportComponent),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            {
              label: 'creditSalesReport/title',
              link: 'credit-sales-report',
            },
          ],
        },
      },
      {
        path: 'settings/working-hours',
        loadComponent: () =>
          import(
            './features/settings/working-hours/working-hours.component'
          ).then((m) => m.WorkingHoursComponent),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'shared/workingHours', link: '/settings/working-hours' },
          ],
        },
      },
      {
        path: 'messages',
        loadChildren: () =>
          import('./features/messages/messages.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: {
          breadcrumbs: [
            { label: 'technicalSupportRequests', link: 'messages' },
          ],
        },
      },
      {
        path: 'notification',
        loadComponent: () =>
          import('./features/notification/all/all.component').then(
            (m) => m.AllComponent
          ),
        data: {
          breadcrumbs: [
            { label: 'shared/notifications', link: 'notification' },
          ],
        },
      },
    ],
  },

  { path: '**', redirectTo: '' },
];
